import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { formatCurrency } from '../../services/api';
import { generateNormalReceipt, generateDuplicateReceipt } from '../../utils/receiptGeneratorFinal';
import {
  ArrowLeft,
  Search,
  User,
  CreditCard,
  CheckCircle,
  XCircle,
  Printer
} from 'lucide-react';

/**
 * PAGE: PAIEMENT TIERS (AGENT)
 * 
 * Formulaire de paiement pour des mutualistes
 */

const PaymentThirdParty = () => {
  const navigate = useNavigate();
  
  // État du formulaire
  const [step, setStep] = useState(1); // 1: Sélection mutualiste, 2: Sélection contributions, 3: Confirmation
  const [searchTerm, setSearchTerm] = useState('');
  const [mutualistes, setMutualistes] = useState([]);
  const [selectedMutualiste, setSelectedMutualiste] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [contributions, setContributions] = useState([]);
  const [selectedContributions, setSelectedContributions] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState('ESPECES');
  const [isLoading, setIsLoading] = useState(false);
  const [paymentReceipt, setPaymentReceipt] = useState(null);

  // Recherche de mutualistes
  const searchMutualistes = async () => {
    if (!searchTerm || searchTerm.length < 2) {
      setMutualistes([]);
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/mutualistes/search?search=${searchTerm}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      console.log('Search response:', response.data);
      setMutualistes(response.data.data?.mutualistes || []);
    } catch (error) {
      console.error('Erreur recherche mutualistes:', error);
      toast.error('Erreur lors de la recherche');
    } finally {
      setIsLoading(false);
    }
  };

  // Charger les polices du mutualiste
  const loadMutualistePolicies = async (mutualisteId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/policies/mutualiste/${mutualisteId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      setPolicies(response.data.data || []);
    } catch (error) {
      console.error('Erreur chargement polices:', error);
      toast.error('Erreur lors du chargement des polices');
    } finally {
      setIsLoading(false);
    }
  };

  // Charger les contributions impayées d'une police
  const loadUnpaidContributions = async (policyId) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/contributions/policy/${policyId}/unpaid`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      // Trier par date décroissante
      const sorted = (response.data.data || []).sort((a, b) => 
        new Date(b.dueDate) - new Date(a.dueDate)
      );
      setContributions(sorted);
    } catch (error) {
      console.error('Erreur chargement contributions:', error);
      toast.error('Erreur lors du chargement des contributions');
    } finally {
      setIsLoading(false);
    }
  };

  // Sélection mutualiste
  const handleSelectMutualiste = (mutualiste) => {
    setSelectedMutualiste(mutualiste);
    setMutualistes([]);
    setSearchTerm('');
    loadMutualistePolicies(mutualiste.id);
    setStep(2);
  };

  // Sélection police
  const handleSelectPolicy = (policy) => {
    setSelectedPolicy(policy);
    loadUnpaidContributions(policy.id);
  };

  // Toggle sélection contribution
  const toggleContribution = (contributionId) => {
    setSelectedContributions(prev => {
      if (prev.includes(contributionId)) {
        return prev.filter(id => id !== contributionId);
      } else {
        return [...prev, contributionId];
      }
    });
  };

  // Calcul du montant total
  const calculateTotalAmount = () => {
    return contributions
      .filter(c => selectedContributions.includes(c.id))
      .reduce((sum, c) => sum + (parseFloat(c.amount) || 0), 0);
  };

  // Libellé du mode de paiement
  const getPaymentMethodLabel = (method) => {
    const labels = {
      'CASH': 'Espèces',
      'ESPECES': 'Espèces',
      'CARTE_BANCAIRE': 'Carte bancaire',
      'VIREMENT': 'Virement',
      'CHEQUE': 'Chèque',
      'ORANGE_MONEY': 'Orange Money',
      'MTN_MONEY': 'MTN Money',
      'MOOV_MONEY': 'Moov Money',
      'WAVE': 'Wave',
      'MOBILE_MONEY': 'Mobile Money'
    };
    return labels[method] || method;
  };

  // Valider le paiement
  const handleValidatePayment = async () => {
    if (selectedContributions.length === 0) {
      toast.error('Veuillez sélectionner au moins une contribution');
      return;
    }

    try {
      setIsLoading(true);
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/payments/third-party`,
        {
          mutualisteId: selectedMutualiste.id,
          policyId: selectedPolicy.id,
          contributionIds: selectedContributions,
          paymentMethod,
          totalAmount: calculateTotalAmount()
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      setPaymentReceipt(response.data.data);
      setStep(3);
      toast.success('Paiement enregistré avec succès');
    } catch (error) {
      console.error('Erreur paiement:', error);
      toast.error(error.response?.data?.error || 'Erreur lors du paiement');
    } finally {
      setIsLoading(false);
    }
  };

  // Générer et imprimer le reçu en PDF
  const handlePrintReceipt = async () => {
    if (!paymentReceipt) return;

    try {
      // Générer le reçu normal (sans filigrane DUPLICATA)
      await generateNormalReceipt(paymentReceipt);
      
      toast.success('Reçu téléchargé avec succès');
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      toast.error('Erreur lors de la génération du reçu');
    }
  };

  // Réinitialiser
  const handleReset = () => {
    setStep(1);
    setSelectedMutualiste(null);
    setSelectedPolicy(null);
    setPolicies([]);
    setContributions([]);
    setSelectedContributions([]);
    setPaymentMethod('ESPECES');
    setPaymentReceipt(null);
  };

  // Recherche auto
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        searchMutualistes();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  return (
    <div className="space-y-6">
      {/* Bouton retour */}
      <button
        onClick={() => navigate('/admin/payments/mutualistes')}
        className="flex items-center text-gray-600 hover:text-primary-600 transition-colors duration-200"
      >
        <ArrowLeft className="h-5 w-5 mr-2" />
        <span className="text-sm font-medium">Retour</span>
      </button>

      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-center">
          <div className="p-3 rounded-lg bg-blue-100 text-blue-600 mr-4">
            <CreditCard className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-primary-900 mb-1">
              Paiement Tiers
            </h1>
            <p className="text-gray-600">
              Enregistrer un paiement pour le compte d'un mutualiste
            </p>
          </div>
        </div>
      </div>

      {/* ÉTAPE 1: Sélection du mutualiste */}
      {step === 1 && (
        <div className="bg-white rounded-xl shadow-card p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <User className="h-5 w-5 mr-2 text-blue-600" />
            Rechercher un mutualiste
          </h2>

          <div className="relative">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                placeholder="Rechercher par matricule ou nom..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Résultats de recherche */}
            {mutualistes.length > 0 && (
              <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
                {mutualistes.map((mutualiste) => (
                  <button
                    key={mutualiste.id}
                    onClick={() => handleSelectMutualiste(mutualiste)}
                    className="w-full p-4 hover:bg-blue-50 transition-colors duration-200 text-left border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-semibold text-gray-900">
                      {mutualiste.nom} {mutualiste.prenoms}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      Matricule: {mutualiste.matricule}
                    </div>
                    {mutualiste.enterprise && (
                      <div className="text-xs text-gray-500 mt-1">
                        {mutualiste.enterprise.nom}
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}

            {isLoading && searchTerm && (
              <div className="text-center py-4 text-gray-500">
                Recherche en cours...
              </div>
            )}
          </div>
        </div>
      )}

      {/* ÉTAPE 2: Sélection des contributions */}
      {step === 2 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* COLONNE GAUCHE (50%) */}
          <div className="space-y-6">
            {/* Infos mutualiste sélectionné */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                <User className="h-4 w-4 mr-2 text-blue-600" />
                Mutualiste sélectionné
              </h3>
              <div className="flex justify-between items-start">
                <div>
                  <div className="font-semibold text-gray-900">
                    {selectedMutualiste?.nom} {selectedMutualiste?.prenoms}
                  </div>
                  <div className="text-sm text-gray-600 mt-1">
                    Matricule: {selectedMutualiste?.matricule}
                  </div>
                  {selectedMutualiste?.enterprise && (
                    <div className="text-xs text-gray-500 mt-1">
                      {selectedMutualiste.enterprise.nom}
                    </div>
                  )}
                </div>
                <button
                  onClick={() => {
                    setStep(1);
                    setSelectedMutualiste(null);
                    setPolicies([]);
                  }}
                  className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                >
                  Changer
                </button>
              </div>
            </div>

            {/* Produit sélectionné */}
            {selectedPolicy ? (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                  <CreditCard className="h-4 w-4 mr-2 text-blue-600" />
                  Produit sélectionné
                </h3>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="font-semibold text-gray-900">
                      {selectedPolicy.policyNumber}
                    </div>
                    <div className="text-sm text-gray-600 mt-1">
                      {selectedPolicy.subscription?.offer?.name}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Montant mensuel: {selectedPolicy.monthlyAmount?.toLocaleString('fr-FR')} FCFA
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      setSelectedPolicy(null);
                      setContributions([]);
                      setSelectedContributions([]);
                    }}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    Changer
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow-card p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <CreditCard className="h-5 w-5 mr-2 text-blue-600" />
                  Sélectionner un produit
                </h2>

                {policies.length === 0 && !isLoading && (
                  <div className="text-center py-8 text-gray-500">
                    Aucune police active pour ce mutualiste
                  </div>
                )}

                <div className="space-y-3 max-h-64 overflow-y-auto">
                  {policies.map((policy) => (
                    <button
                      key={policy.id}
                      onClick={() => handleSelectPolicy(policy)}
                      className="w-full p-4 rounded-lg border-2 border-gray-200 hover:border-blue-300 transition-all duration-200 text-left"
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <div className="font-semibold text-gray-900">
                            {policy.policyNumber}
                          </div>
                          <div className="text-sm text-gray-600 mt-1">
                            {policy.subscription?.offer?.name}
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Montant mensuel: {policy.monthlyAmount?.toLocaleString('fr-FR')} FCFA
                          </div>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Section paiement */}
            {selectedPolicy && contributions.length > 0 && selectedContributions.length > 0 && (
              <div className="bg-white rounded-xl shadow-card p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">
                  Détails du paiement
                </h2>

                {/* Montant total */}
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                  <div className="flex justify-between items-center">
                    <span className="text-lg font-semibold text-gray-900">
                      Montant total
                    </span>
                    <span className="text-2xl font-bold text-green-600">
                      {formatCurrency(calculateTotalAmount())}
                    </span>
                  </div>
                  <div className="text-sm text-gray-600 mt-2">
                    {selectedContributions.length} paiement(s) sélectionné(s)
                  </div>
                </div>

                {/* Mode de paiement */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mode de paiement
                  </label>
                  <select
                    value={paymentMethod}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="ESPECES">Espèces</option>
                    <option value="VIREMENT">Virement</option>
                    <option value="CHEQUE">Chèque</option>
                    <option value="CARTE_BANCAIRE">Carte bancaire</option>
                  </select>
                </div>

                {/* Boutons d'action */}
                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => {
                      setSelectedPolicy(null);
                      setContributions([]);
                      setSelectedContributions([]);
                    }}
                    className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
                  >
                    <XCircle className="h-5 w-5 inline mr-2" />
                    Annuler
                  </button>
                  <button
                    onClick={handleValidatePayment}
                    disabled={selectedContributions.length === 0 || isLoading}
                    className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors duration-200"
                  >
                    <CheckCircle className="h-5 w-5 inline mr-2" />
                    {isLoading ? 'Traitement...' : 'Valider le paiement'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* COLONNE DROITE (50%) - Paiements */}
          {selectedPolicy && contributions.length > 0 && (
            <div>
              <div className="bg-white rounded-xl shadow-card p-6">
                <div className="space-y-2">
                  {contributions.map((contribution) => (
                    <label
                      key={contribution.id}
                      className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                    >
                      <div className="flex items-center flex-1 min-w-0">
                        <input
                          type="checkbox"
                          checked={selectedContributions.includes(contribution.id)}
                          onChange={() => toggleContribution(contribution.id)}
                          className="h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded flex-shrink-0"
                        />
                        <div className="ml-3 flex items-center gap-2 min-w-0">
                          <span className="font-medium text-gray-900">
                            {new Date(contribution.dueDate).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long'
                            })}
                          </span>
                          {contribution.status === 'OVERDUE' && (
                            <span className="inline-block px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800 flex-shrink-0">
                              Retard
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="font-bold text-base text-blue-600 ml-4 flex-shrink-0">
                        {formatCurrency(contribution.amount)}
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ÉTAPE 3: Reçu de paiement */}
      {step === 3 && paymentReceipt && (
        <div className="bg-white rounded-xl shadow-card p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Paiement Enregistré
            </h2>
            <p className="text-gray-600">
              Le paiement a été enregistré avec succès
            </p>
          </div>

          {/* Détails du reçu */}
          <div className="border-2 border-gray-200 rounded-lg p-6 mb-6">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900">REÇU DE PAIEMENT</h3>
              <p className="text-sm text-gray-600 mt-1">
                N° {paymentReceipt.receiptNumber}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {new Date(paymentReceipt.paymentDate).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>

            <div className="space-y-3 border-t border-b border-gray-200 py-4 my-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Mutualiste:</span>
                <span className="font-semibold">
                  {selectedMutualiste?.nom} {selectedMutualiste?.prenoms}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Matricule:</span>
                <span className="font-semibold">{selectedMutualiste?.matricule}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Police:</span>
                <span className="font-semibold">{selectedPolicy?.policyNumber}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Produit:</span>
                <span className="font-semibold">
                  {paymentReceipt.policy?.offerName}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Mode de paiement:</span>
                <span className="font-semibold">{getPaymentMethodLabel(paymentReceipt.paymentMethod)}</span>
              </div>
              {paymentReceipt?.transactionRef && (
                <div className="flex justify-between">
                  <span className="text-gray-600">Référence:</span>
                  <span className="font-semibold">{paymentReceipt.transactionRef}</span>
                </div>
              )}
            </div>

            <div className="bg-green-50 rounded-lg p-4 mt-4">
              <div className="flex justify-between items-center">
                <span className="text-lg font-semibold text-gray-900">
                  MONTANT TOTAL
                </span>
                <span className="text-3xl font-bold text-green-600">
                  {formatCurrency(paymentReceipt.amount)}
                </span>
              </div>
            </div>

            <div className="mt-4 text-xs text-gray-500 text-center">
              {selectedContributions.length} paiement(s) réglé(s)
            </div>
          </div>

          {/* Boutons d'action */}
          <div className="flex gap-3">
            <button
              onClick={handlePrintReceipt}
              className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors duration-200"
            >
              <Printer className="h-5 w-5 inline mr-2" />
              Imprimer le reçu
            </button>
            <button
              onClick={handleReset}
              className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Nouveau paiement
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentThirdParty;
